<template>
  <div class="relative h-full">
    <nav
      :class="['bg-white relative border-neutral-200 mb-4 transition-all duration-300 h-full', isCollapsed ? 'w-[40px]' : 'w-[270px]']"
    >
      <div class="flex flex-col h-full relative">
        <div :class="['flex items-center justify-between gap-2 p-2', isCollapsed ? 'flex-col' : 'flex-row']">
          <router-link
            v-if="currentUser.start_module?.route"
            :to="'/' + currentUser.start_module.route"
            class="flex justify-center"
          >
            <logo class="w-8 h-8" />
          </router-link>
          <button @click="toggleCollapse" class="p-1 rounded hover:bg-neutral-100">
            <svg
              width="40"
              height="40"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              :class="{'rotate-180': isCollapsed, 'transition-transform duration-300': true}"
            >
              <path d="M17.9993 10.3334V21.6667M14.666 18L12.666 16L14.666 14"
                    stroke="#020618"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
              />
              <path d="M10 16C10 13.1716 10 11.7574 10.8787 10.8787C11.7574 10 13.1716 10 16 10C18.8284 10 20.2426 10 21.1213 10.8787C22 11.7574 22 13.1716 22 16C22 18.8284 22 20.2426 21.1213 21.1213C20.2426 22 18.8284 22 16 22C13.1716 22 11.7574 22 10.8787 21.1213C10 20.2426 10 18.8284 10 16Z"
                    stroke="#020618"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <!-- Фиксированный пункт «Уведомления» с бейджем непрочитанных -->
        <router-link
          to="/notifications"
          :class="['flex items-center w-full gap-1 p-2 rounded transition-colors duration-150', $route.path === '/notifications' ? 'bg-neutral-100' : 'hover:bg-neutral-50']"
          @mouseenter="showTooltip($event, 'Уведомления')"
          @mouseleave="hideTooltip"
        >
          <div class="shrink-0 w-6 flex justify-center relative">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span
              v-if="unreadCount > 0 && isCollapsed"
              class="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] leading-none font-semibold text-white bg-red-500 rounded-full"
            >
              {{ unreadCount > 99 ? '99+' : unreadCount }}
            </span>
          </div>
          <span v-if="!isCollapsed" class="ml-1 whitespace-nowrap">Уведомления</span>
          <span
            v-if="!isCollapsed && unreadCount > 0"
            class="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[11px] leading-none font-semibold text-white bg-red-500 rounded-full"
          >
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
        </router-link>
        <button
          v-if="canScrollUp"
          type="button"
          @click="scrollNav(-1)"
          class="shrink-0 w-full h-5 flex items-center justify-center hover:bg-neutral-100 transition-colors rounded"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <div ref="scrollContainer" class="navigation overflow-y-auto overflow-x-hidden flex-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" @scroll="updateScrollState">
          <template v-for="section in scrollSections" :key="section.title">
            <hr class="border-neutral-300 my-4" />
            <ul :class="['w-full flex flex-col text-neutral-900 text-md font-medium', isCollapsed ? 'items-center' : 'items-start']">
              <template v-for="(item, index) in section.items" :key="item.type === 'group' ? 'g-' + item.id : 'm-' + index">
                <!-- Раскрывающаяся группа -->
                <li
                  v-if="item.type === 'group'"
                  :class="['group relative w-full', isCollapsed ? '' : 'w-full']"
                  @mouseenter="showTooltip($event, item.name)"
                  @mouseleave="hideTooltip"
                >
                  <div
                    :class="[
                      'flex items-center w-full rounded transition-colors duration-150',
                      $route.name === 'ModuleGroup' && $route.params.groupId === String(item.id)
                        ? 'bg-neutral-100'
                        : 'hover:bg-neutral-50'
                    ]"
                  >
                    <router-link
                      :to="`/module-group/${item.id}`"
                      class="flex items-center flex-1 min-w-0 p-2 gap-1"
                    >
                      <div class="shrink-0 w-6 flex justify-center">
                        <component :is="getIconComponent(item.key)" class="w-5 h-5" />
                      </div>
                      <span
                        v-if="!isCollapsed"
                        class="transition-all duration-300 whitespace-nowrap truncate"
                        :class="{'ml-1': !isCollapsed, 'max-w-0': isCollapsed}"
                      >
                        {{ item.name }}
                      </span>
                    </router-link>
                    <button
                      v-if="!isCollapsed"
                      type="button"
                      @click.stop="toggleGroupExpand(item.id)"
                      class="p-2 shrink-0 rounded transition-colors duration-150"
                    >
                      <svg
                        class="w-4 h-4 transition-transform duration-200"
                        :class="{ 'rotate-180': isGroupExpanded(item.id) }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  <ul v-if="!isCollapsed && isGroupExpanded(item.id)" class="pl-2 mt-0 border-l border-neutral-200 ml-1">
                    <li
                      v-for="module in item.modules"
                      :key="module.id"
                      class="w-full"
                      @mouseenter="showTooltip($event, module.name)"
                      @mouseleave="hideTooltip"
                    >
                      <router-link
                        :to="`/${module.route}`"
                        :class="[
                          'flex items-center w-full p-2 gap-1 rounded transition-colors duration-150 pl-1',
                          $route.path === '/' + module.route ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                        ]"
                      >
                        <div class="shrink-0 w-6 flex justify-center">
                          <component :is="getIconComponent(module.key)" class="w-5 h-5" />
                        </div>
                        <span class="whitespace-nowrap text-sm">{{ module.name }}</span>
                      </router-link>
                    </li>
                  </ul>
                </li>
                <!-- Обычный модуль (без группы) -->
                <li
                  v-else
                  :class="['group relative w-full', isCollapsed ? '' : 'w-full']"
                  @mouseenter="showTooltip($event, item.module.name)"
                  @mouseleave="hideTooltip"
                >
                  <router-link
                    :to="item.module.route ? `/${item.module.route}` : '#'"
                    @click.prevent="item.module.key === 'logout' ? logoutUser() : null"
                    :class="[
                      'flex items-center w-full p-2 gap-1 rounded transition-colors duration-150',
                      $route.path === '/' + item.module.route ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                    ]"
                  >
                    <div class="shrink-0 w-6 flex justify-center">
                      <component :is="getIconComponent(item.module.key)" class="w-5 h-5" />
                    </div>
                    <span
                      v-if="!isCollapsed"
                      class="transition-all duration-300 whitespace-nowrap"
                      :class="{'ml-1': !isCollapsed, 'max-w-0': isCollapsed, 'max-w-[180px]': !isCollapsed}"
                    >
                      {{ item.module.name }}
                    </span>
                  </router-link>
                </li>
              </template>
            </ul>
          </template>
          <hr class="border-neutral-300 my-4" />
        </div>
        <button
          v-if="canScrollDown"
          type="button"
          @click="scrollNav(1)"
          class="shrink-0 w-full h-5 flex items-center justify-center hover:bg-neutral-100 transition-colors rounded"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div v-if="bottomSection" class="shrink-0">
          <ul :class="['w-full flex flex-col text-neutral-900 text-md font-medium', isCollapsed ? 'items-center' : 'items-start']">
            <li
              v-for="(item, index) in bottomSection.items"
              :key="'bottom-' + index"
              :class="['group relative w-full', isCollapsed ? '' : 'w-full']"
              @mouseenter="showTooltip($event, item.module.name)"
              @mouseleave="hideTooltip"
            >
              <router-link
                :to="item.module.route ? `/${item.module.route}` : '#'"
                @click.prevent="item.module.key === 'logout' ? logoutUser() : null"
                :class="[
                  'flex items-center w-full p-2 gap-1 rounded transition-colors duration-150',
                  $route.path === '/' + item.module.route ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                ]"
              >
                <div class="shrink-0 w-6 flex justify-center">
                  <component :is="getIconComponent(item.module.key)" class="w-5 h-5" />
                </div>
                <span
                  v-if="!isCollapsed"
                  class="transition-all duration-300 whitespace-nowrap"
                  :class="{'ml-1': !isCollapsed, 'max-w-0': isCollapsed, 'max-w-[180px]': !isCollapsed}"
                >
                  {{ item.module.name }}
                </span>
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- Global tooltip -->
    <div
      v-if="tooltip.visible && isCollapsed"
      :style="{
        position: 'fixed',
        top: tooltip.top + 'px',
        left: tooltip.left + 'px'
      }"
      class="z-50 bg-neutral-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap pointer-events-none transition-opacity duration-150"
    >
      {{ tooltip.text }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useNotificationsStore } from '@/stores/notifications.js'
import Logo from '@/components/Logo.vue'
import DeskIcon from '@/components/navigationIcons/DeskIcon.vue'
import BookIcon from '@/components/navigationIcons/BookIcon.vue'
import StorehouseIcon from '@/components/navigationIcons/StorehouseIcon.vue'
import OrdersIcon from '@/components/navigationIcons/OrdersIcon.vue'
import DefaultIcon from '@/components/navigationIcons/DefaultIcon.vue'
import ProductionIcon from '@/components/navigationIcons/ProductionIcon.vue'
import ProfileIcon from '@/components/navigationIcons/ProfileIcon.vue'
import LogoutIcon from '@/components/navigationIcons/LogoutIcon.vue'
import ReceiptInvoiceControlIcon from '@/components/navigationIcons/ReceiptInvoiceControlIcon.vue'
import SettingsIcon from '@/components/navigationIcons/SettingsIcon.vue'

export default {
  components: {
    LogoutIcon,
    ProfileIcon,
    ProductionIcon,
    DefaultIcon,
    OrdersIcon,
    StorehouseIcon,
    BookIcon,
    Logo,
    DeskIcon,
    ReceiptInvoiceControlIcon,
    SettingsIcon
  },
  data() {
    return {
      isCollapsed: JSON.parse(localStorage.getItem('sidebar-collapsed')) || false,
      expandedGroups: JSON.parse(localStorage.getItem('nav-expanded-groups') || '{}'),
      tooltip: {
        text: '',
        top: 0,
        left: 0,
        visible: false
      },
      canScrollUp: false,
      canScrollDown: false,
      resizeObserver: null
    }
  },
  mounted() {
    this.$nextTick(this.updateScrollState)
    window.addEventListener('resize', this.updateScrollState)
    // ResizeObserver отслеживает изменения размеров контента (раскрытие групп, сворачивание меню и т.п.)
    if (this.$refs.scrollContainer && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.updateScrollState)
      this.resizeObserver.observe(this.$refs.scrollContainer)
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateScrollState)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  watch: {
    scrollSections() {
      this.$nextTick(this.updateScrollState)
    },
    isCollapsed() {
      this.$nextTick(this.updateScrollState)
    }
  },
  computed: {
    ...mapStores(useAuthStore, useNotificationsStore),
    currentUser() {
      return this.authStore?.currentUser
    },
    unreadCount() {
      return this.notificationsStore?.unreadCount || 0
    },
    sections() {
      const deskModules = this.currentUser?.modules.filter(m =>
        ['assembler-desk', 'picking-desk', 'otk-desk', 'packer-desk', 'smk-desk'].includes(m.key)
      ) || []
      const otherModules = this.currentUser?.modules.filter(m =>
        !['assembler-desk', 'picking-desk', 'otk-desk', 'packer-desk', 'smk-desk'].includes(m.key)
      ) || []

      // Разделы администрирования: ключ раздела — App\Enums\AdminSections,
      // пункт показывается, если роль даёт доступ к разделу (полный или чтение)
      const adminSections = [
        { name: 'Номенклатура', route: 'products', section: 'products' },
        { name: 'Контрагенты', route: 'companies', section: 'companies' },
        { name: 'Пользователи', route: 'users', section: 'users' },
        { name: 'Модули', route: 'modules', section: 'modules' },
        { name: 'Цеха', route: 'workshops', section: 'workshops' },
        { name: 'Рабочие центры', route: 'workshopPostUnits', section: 'workshop_posts' },
        { name: 'Виды рабочих центров', route: 'workshopPosts', section: 'workshop_post_types' },
        { name: 'Страны', route: 'countries', section: 'countries' },
        { name: 'Организации', route: 'organizations', section: 'organizations' },
        { name: 'Договоры', route: 'contracts', section: 'contracts' },
        { name: 'Ставки НДС', route: 'vat-rates', section: 'vat_rates' },
        { name: 'Заказы поставщику', route: 'supplier-orders', section: 'supplier_orders' },
        { name: 'Отчёты ПСИ и ВХК', route: 'otk-reports', section: 'otk_reports' },
        { name: 'Роли', route: 'roles', section: 'roles' }
      ]

      const adminModules = adminSections
        .filter(item => this.authStore.canSection(item.section))
        .map(item => ({ name: item.name, route: item.route, key: 'admin' }))

      if (this.currentUser?.email === 'admin@admin.ru') {
        adminModules.push({ name: 'Запросы 1С', route: 'onecRequests', key: 'admin' })
      }
      if (this.currentUser?.email === 'admin@admin.ru') {
        adminModules.push({ name: 'Резервные копии', route: 'admin/backup', key: 'backup' })
      }
      if (this.currentUser?.email === 'admin@admin.ru') {
        adminModules.push({ name: 'Единицы продукции', route: 'batch-products', key: 'admin' })
      }

      const settingsModules = [
        { name: 'Настройки навигации', route: 'navigation-settings', key: 'settings' }
      ]

      const commonModules = [
        { name: 'Профиль', route: 'user', key: 'profile' },
        { name: 'Выйти', route: null, key: 'logout' }
      ]

      const grouped = {}
      const ungrouped = []
      otherModules.forEach((m) => {
        if (m.module_group_id && m.module_group) {
          const gid = m.module_group_id
          if (!grouped[gid]) {
            grouped[gid] = {
              type: 'group',
              id: gid,
              name: m.module_group.name,
              key: m.module_group.key || m.key,
              modules: []
            }
          }
          grouped[gid].modules.push(m)
        } else {
          ungrouped.push({ type: 'module', module: m })
        }
      })

      // Сортируем модули внутри каждой группы (на всякий случай, хотя они уже отсортированы с бэкенда)
      Object.values(grouped).forEach(group => {
        group.modules.sort((a, b) => {
          const keyCompare = (a.key || '').localeCompare(b.key || '')
          if (keyCompare !== 0) return keyCompare
          return (a.name || '').localeCompare(b.name || '')
        })
      })

      // Объединяем группы и модули без группы, сортируем вместе по key, затем по name
      const allItems = [
        ...Object.values(grouped),
        ...ungrouped
      ].sort((a, b) => {
        const aKey = a.type === 'group' ? (a.key || '') : (a.module?.key || '')
        const bKey = b.type === 'group' ? (b.key || '') : (b.module?.key || '')
        const keyCompare = aKey.localeCompare(bKey)
        if (keyCompare !== 0) return keyCompare

        const aName = a.type === 'group' ? (a.name || '') : (a.module?.name || '')
        const bName = b.type === 'group' ? (b.name || '') : (b.module?.name || '')

        // Надстройка: фиксированный порядок для складских модулей/групп
        // Чтобы отключить — удалить блок ниже до «конец надстройки»
        if (aKey === 'storehouse' && bKey === 'storehouse') {
          return this.getStorehouseSortOrder(aName) - this.getStorehouseSortOrder(bName)
        }
        // конец надстройки

        return aName.localeCompare(bName)
      })

      const otherItems = allItems

      const sections = []
      if (deskModules.length) {
        sections.push({ title: 'Рабочие модули', items: deskModules.map(m => ({ type: 'module', module: m })) })
      }
      if (otherItems.length) {
        sections.push({ title: 'Остальные модули', items: otherItems })
      }
      if (adminModules.length) {
        sections.push({ title: 'Администрирование', items: adminModules.map(m => ({ type: 'module', module: m })) })
      }
      if (this.currentUser?.email === 'admin@admin.ru') {
        sections.push({ title: 'Настройки', items: settingsModules.map(m => ({ type: 'module', module: m })) })
      }
      sections.push({ title: 'Общее', items: commonModules.map(m => ({ type: 'module', module: m })) })

      return sections
    },
    // Последняя секция («Общее» — Профиль/Выйти) — рендерится отдельным блоком снизу
    bottomSection() {
      const all = this.sections
      return all.length ? all[all.length - 1] : null
    },
    // Все секции, кроме последней — идут в скроллируемую область
    scrollSections() {
      const all = this.sections
      return all.length > 1 ? all.slice(0, -1) : []
    }
  },
  methods: {
    ...mapActions(useAuthStore, ['logout']),
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed
      localStorage.setItem('sidebar-collapsed', JSON.stringify(this.isCollapsed))
    },
    logoutUser() {
      this.logout().then(() => {
        this.$router.push({ name: 'Login' })
      })
    },
    getIconComponent(key) {
      switch (key) {
        case 'assembler-desk':
        case 'picking-desk':
        case 'otk-desk':
        case 'packer-desk':
        case 'smk-desk':
          return 'DeskIcon'
        case 'storehouse':
          return 'StorehouseIcon'
        case 'orders':
          return 'OrdersIcon'
        case 'production':
          return 'ProductionIcon'
        case 'receipt-invoice-control':
          return 'ReceiptInvoiceControlIcon'
        case 'backup':
          return 'DefaultIcon'
        case 'profile':
          return 'ProfileIcon'
        case 'logout':
          return 'LogoutIcon'
        case 'settings':
          return 'SettingsIcon'
        default:
          return 'DefaultIcon'
      }
    },
    /**
     * Надстройка: фиксированный порядок складских модулей/групп.
     * Чтобы отключить — удалить этот метод и блок сортировки в sections.
     */
    getStorehouseSortOrder(name) {
      const order = {
        'Основной склад': 1,
        'Склад производство': 2,
        'Склад ВХК': 3,
        'Склад ОТК': 4,
        'Склад готовой продукции': 5,
      }
      return order[name] ?? 99
    },
    toggleGroupExpand(groupId) {
      const key = String(groupId)
      this.expandedGroups[key] = !this.expandedGroups[key]
      localStorage.setItem('nav-expanded-groups', JSON.stringify(this.expandedGroups))
      this.$nextTick(this.updateScrollState)
    },
    isGroupExpanded(groupId) {
      return !!this.expandedGroups[String(groupId)]
    },
    showTooltip(event, text) {
      const rect = event.currentTarget.getBoundingClientRect()
      this.tooltip.text = text
      this.tooltip.top = rect.top + rect.height / 5
      this.tooltip.left = rect.right + 8
      this.tooltip.visible = true
    },
    hideTooltip() {
      this.tooltip.visible = false
    },
    // Пересчёт видимости кнопок скролла
    updateScrollState() {
      const el = this.$refs.scrollContainer
      if (!el) {
        this.canScrollUp = false
        this.canScrollDown = false
        return
      }
      this.canScrollUp = el.scrollTop > 0
      this.canScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 1
    },
    // Прокрутка скролл-контейнера: direction = -1 вверх, 1 вниз
    scrollNav(direction) {
      const el = this.$refs.scrollContainer
      if (!el) return
      const step = Math.max(80, Math.floor(el.clientHeight * 0.4))
      el.scrollBy({ top: direction * step, behavior: 'smooth' })
    }
  }
}
</script>
